import { ReactNode, useMemo } from 'react';
import { Map } from '@ais-ui/map';
import { MapComponents } from '../modules/Map/MapComponents';
import { BaseMap } from '../modules/Map/BaseMap';
import { useSettings } from '../modules/Settings/SettingsProvider';
import { useReplayContext } from '../modules/Replay/ReplayContext';
import { cn } from '@ais-ui/utils';
import { LoadingSpinner } from '@ais-ui/ui-core';
import { useReportContext } from '../modules/Reports/ReportContext';
import { toLonLat } from 'ol/proj';

export interface MapProps {
  children?: ReactNode;
  hideOsm?: boolean;
}

export interface PageProps {
  includesMap?: boolean;
  children?: ReactNode;
}

export const MapPage = ({ children }: PageProps) => {
  const { settings, olMapRef, mapDetails } = useSettings();
  const { hasLoaded: hasLoadedReplay, isWaiting: isWaitingOnReplay } =
    useReplayContext();
  const { hasLoaded: hasLoadedReport, isWaiting: isWaitingOnReport } =
    useReportContext();

  const center = useMemo(() => {
    if (mapDetails.center && mapDetails.center.length === 2) {
      return [
        toLonLat(mapDetails.center)[0],
        toLonLat(mapDetails.center)[1],
      ] as [number, number];
    }

    if (settings.homeBookmark?.lon && settings.homeBookmark?.lat) {
      return [settings.homeBookmark.lon, settings.homeBookmark.lat] as [
        number,
        number
      ];
    }
    return undefined;
  }, [mapDetails.center, settings.homeBookmark]);

  const zoom = useMemo(() => {
    if (mapDetails.zoom) {
      return mapDetails.zoom;
    }

    return settings.homeBookmark?.zoomLevel || undefined;
  }, [mapDetails.zoom, settings.homeBookmark]);

  const showReplayLoad = isWaitingOnReplay && !hasLoadedReplay;
  const showReportLoad = isWaitingOnReport && !hasLoadedReport;

  return (
    <Map
      olMapRef={olMapRef}
      center={center}
      zoom={zoom}
      className={cn(
        showReplayLoad && 'grayscale-[50%]',
        showReportLoad && 'grayscale-[50%]'
      )}
    >
      <BaseMap></BaseMap>
      <MapComponents>{children}</MapComponents>
      {(showReplayLoad || showReportLoad) && (
        <div className="absolute top-1/2 left-1/2">
          <LoadingSpinner></LoadingSpinner>
        </div>
      )}
    </Map>
  );
};
